import { pluralRu } from "@/shared/lib/utils";
import { parseServerDate } from "@/shared/lib/serverDate";
import { replaceSectionFailureText, replaceSectionReason, } from "@/shared/lib/editTrack";
import { trackActionReasonMessage } from "@/shared/lib/trackActionErrors";
import { isKnownAppRoute, routePathOf } from "./appRoutes";
/** Число из payload (бэк может прислать строку) — иначе null. */
const readNumber = (raw) => {
    const value = typeof raw === "string" ? Number(raw) : raw;
    return typeof value === "number" && Number.isFinite(value) && value > 0
        ? value
        : null;
};
/** audio_id песни из payload — единственный достоверный источник (см. ниже). */
const readAudioId = (payload) => readNumber(payload?.audio_id);
/**
 * Маршруты, бесполезные без обязательного query-параметра: путь объявлен в
 * роутере, но страница без него открывается в состоянии «нечего показывать».
 * `/release` резолвит заявку ПО ТРЕКУ, поэтому голый `/release` из `meta.route`
 * (бэк отдаёт его, когда в payload нет `audio_id`) — не цель, а тупик: такой
 * маршрут не уважаем и уходим в фолбэк по `entity`, который вернёт `null`.
 */
const REQUIRED_ROUTE_PARAMS = {
    "/release": "audioId",
};
/**
 * Сущности, для которых маршрут ФРОНТ строит сам, а `meta.route` из БД может
 * быть протухшим. `text_*` до ветки notification-type-metadata писался с
 * `route: "/my-songs"` — путь валидный, `isKnownAppRoute` его пропускает, и весь
 * УЖЕ НАКОПЛЕННЫЙ корпус `text_ready`/`text_failed` (TTL и чисток у уведомлений
 * нет) навсегда уводил бы в «Мои песни» вместо сохранённого текста. Актуальный
 * бэк для `entity=text` порождает только `/generate-song?text=<id>`, поэтому
 * любой другой маршрут у этой сущности считаем legacy и игнорируем.
 */
const ENTITY_ROUTE_SHAPE = {
    text: /^\/generate-song(\?|$)/,
};
/** Пригоден ли `meta.route` к использованию как есть (см. таблицы выше). */
const isUsableMetaRoute = (route, entity) => {
    if (!isKnownAppRoute(route))
        return false;
    const query = route.split("#")[0].split("?")[1] ?? "";
    const requiredParam = REQUIRED_ROUTE_PARAMS[routePathOf(route)];
    if (requiredParam && !new URLSearchParams(query).get(requiredParam)) {
        return false;
    }
    const shape = entity ? ENTITY_ROUTE_SHAPE[entity] : undefined;
    if (shape && !shape.test(route))
        return false;
    return true;
};
/**
 * Куда вести по клику на уведомление.
 *
 * Источник маршрута — `meta.route` от бэка: он знает контекст события (какой
 * баланс пополнили, какой трек готов) и строит хвост query сам. Уважаем его
 * ТОЛЬКО если такой путь реально объявлен в роутере активного шелла
 * (`isKnownAppRoute`), ведёт не в тупик и не противоречит тому, как фронт сам
 * адресует эту сущность (`isUsableMetaRoute`) — так битая ссылка на ещё не
 * существующий раздел (сейчас это `/my-releases`) и протухший legacy-маршрут не
 * доедут до пользователя, а откатятся в фолбэк.
 *
 * Дальше — маппинг по `meta.entity` (стабильный контракт бэка), затем фолбэк по
 * `type` (мета есть не во всех ответах — в БД лежат старые уведомления).
 * Отдельных `/subscription` и `/pricing` в приложении нет — подписка/исчерпанный
 * баланс/оплата ведут на `/balance`.
 *
 * `null` — навигации нет (уведомление некликабельно). В частности это ЯВНЫЙ
 * `meta.route === null`: так бэк помечает события без цели (`generation_*`,
 * `system`). Раньше их подхватывал фолбэк и подставлял ЧУЖОЙ идентификатор в
 * `/my-songs?audio=<generation_id>` — клик уводил в никуда.
 */
export const resolveNotificationRoute = (n) => {
    const meta = n.meta ?? undefined;
    const songsRoute = (id) => id != null ? `/my-songs?audio=${id}` : "/my-songs";
    // Генерация песни с преселектом сохранённого голоса (см. GenerateSongPage).
    const voiceRoute = (id) => id != null ? `/generate-song?voice=${id}` : "/generate-song";
    // Продолжение флоу клона голоса на шаге пения контрольной фразы: фронт
    // откроет попап «Мой голос» и resume-рекордер для профиля <id>.
    const voiceVerifyRoute = (id) => id != null ? `/generate-song?voice_verify=${id}` : "/generate-song";
    // Сохранённый текст: «Создание песни» → источник «Сохранённые» с выделением.
    const textRoute = (id) => id != null ? `/generate-song?text=${id}` : "/generate-song";
    // Заявка на релиз трека привязана к аудио — без него страница пустая.
    const releaseRoute = (id) => id != null ? `/release?audioId=${id}` : null;
    if (meta) {
        // Явный null — «навигации нет», фолбэк не запускаем.
        if (meta.route === null)
            return null;
        const route = typeof meta.route === "string" ? meta.route.trim() : "";
        if (route && isUsableMetaRoute(route, meta.entity))
            return route;
    }
    const payloadAudioId = readAudioId(n.payload);
    if (meta?.entity) {
        switch (meta.entity) {
            case "song":
                // ВНИМАНИЕ: для песенных типов бэк кладёт в entity_id **song_id**, а не
                // audio_id (см. build_meta → _SONG_TYPES). Поэтому адресуемся только по
                // настоящему audio_id из payload, иначе `?audio=` получил бы чужой id.
                return songsRoute(payloadAudioId);
            case "audio":
            case "media":
            case "media_output":
                return songsRoute(payloadAudioId ?? meta.entity_id);
            case "voice":
                return voiceRoute(meta.entity_id ?? n.object_id);
            case "voice_verify":
                return voiceVerifyRoute(meta.entity_id ?? n.object_id);
            case "text":
                return textRoute(meta.entity_id ?? n.object_id);
            case "release":
                // ВНИМАНИЕ: у релизов в entity_id лежит **release_order_id** (см.
                // build_meta → _RELEASE_TYPES), а страница адресуется ТРЕКОМ. Поэтому
                // только payload.audio_id; без него цели нет (releaseRoute → null).
                return releaseRoute(payloadAudioId);
            case "payment":
            case "purchase":
            case "balance":
            case "subscription":
                return "/balance";
            case "referral":
                return "/referral";
            case "generation":
            case "system":
                // Цели нет: id генерации/системного события не адресуется ни одним
                // разделом кабинета. Явно возвращаем null, чтобы не свалиться в фолбэк.
                return null;
            default:
                break;
        }
    }
    // Fallback по типу уведомления (бэк-мета отсутствует).
    const t = n.type;
    if (t.startsWith("song_"))
        return songsRoute(payloadAudioId);
    if (t.startsWith("text_"))
        return textRoute(n.object_id);
    // release_* без меты адресовать нечем: object_id — id заявки, а не аудио.
    if (t.startsWith("release_"))
        return null;
    if (t === "voice_ready" || t === "voice_expired")
        return voiceRoute(n.object_id);
    if (t === "voice_phrase_ready")
        return voiceVerifyRoute(n.object_id);
    if (t.startsWith("payment_") || t === "balance_depleted")
        return "/balance";
    if (t.startsWith("subscription_"))
        return "/balance";
    if (t === "referral_gift")
        return "/referral";
    return null;
};
// Медиа-выходы песни (fallback по типу, когда meta.kind/entity нет). Сюда НЕ
// входят song_ready/song_failed (ведут в «Мои песни») и generation_* (цели нет).
const MEDIA_TYPE_RE = /^song_(instrumental|wav|extend|replace_section|video_cover|karaoke)_/;
// kind (из meta или выведенный из типа) → нужный таб хаба.
const kindToTab = (kind) => {
    if (kind && kind.includes("video"))
        return "video";
    if (kind && kind.includes("karaoke"))
        return "karaoke";
    return "song";
};
// Восстанавливаем kind из типа уведомления, когда meta.kind не пришёл.
const inferKind = (type) => {
    if (type.includes("video_cover"))
        return "video_cover";
    if (type.includes("karaoke"))
        return "karaoke";
    if (type.includes("instrumental"))
        return "instrumental";
    if (type.includes("wav"))
        return "wav";
    if (type.includes("extend"))
        return "extend";
    if (type.includes("replace_section"))
        return "replace_section";
    return "song";
};
/**
 * Резолв «куда на хабе» для МЕДИЙНОГО уведомления. Приоритет —
 * `meta.media_output_id` (одиночный вид), иначе таб по `meta.kind`/типу.
 *
 * `audioId` берём из `payload.audio_id` — это единственный достоверный источник:
 * в `meta.entity_id` для песенных типов лежит **song_id** (см. build_meta →
 * _SONG_TYPES), и токен резолвился по чужому идентификатору. Для медиа-типов
 * `entity_id` действительно audio_id — оставляем его запасным вариантом ради
 * старых уведомлений в БД, где `audio_id` в payload ещё не было.
 */
export const getSongNotificationTarget = (n) => {
    const meta = n.meta ?? undefined;
    const entity = meta?.entity;
    // Медийным считаем entity=audio/media/media_output С указанным kind, либо
    // (когда меты нет) сам тип уведомления. entity=song — это готовая песня, она
    // идёт в «Мои песни», а не на хаб.
    const isMediaEntity = (entity === "audio" || entity === "media" || entity === "media_output") &&
        Boolean(meta?.kind ?? MEDIA_TYPE_RE.test(n.type));
    if (entity && !isMediaEntity)
        return null;
    if (!isMediaEntity && !MEDIA_TYPE_RE.test(n.type))
        return null;
    const audioId = readAudioId(n.payload) ?? meta?.entity_id ?? n.object_id;
    if (audioId == null)
        return null;
    if (meta?.media_output_id != null) {
        return { audioId, query: `?media=${meta.media_output_id}` };
    }
    const tab = kindToTab(meta?.kind ?? inferKind(n.type));
    return { audioId, query: `?tab=${tab}` };
};
/** Безопасно читаем человекочитаемое название песни из payload уведомления. */
const readTitle = (payload) => {
    const raw = payload?.title ?? payload?.name;
    return typeof raw === "string" ? raw.trim() : "";
};
/**
 * Обложка песни из payload (`cover_url` кладёт бэк только когда она непустая).
 * Отдаём лишь http(s)/относительные ссылки — мусор из старых уведомлений в
 * карточку не пускаем (см. NotificationBell: миниатюра слева).
 *
 * Отдельно режем protocol-relative (`//evil.com/px.gif` и `/\evil.com/px.gif` —
 * браузер нормализует `\` в `/`): формально это «начинается со слэша», но
 * фактически ЧУЖОЙ хост, замаскированный под относительный путь.
 */
export const readNotificationCover = (payload) => {
    const raw = payload?.cover_url;
    if (typeof raw !== "string")
        return null;
    const url = raw.trim();
    if (!url)
        return null;
    if (/^[\\/]{2}/.test(url))
        return null;
    return /^(https?:\/\/|\/)/i.test(url) ? url : null;
};
/** Человекочитаемый текст generic/system-уведомления из payload (title + message/text). */
const readPayloadContent = (payload) => {
    const p = payload ?? {};
    const str = (v) => typeof v === "string" && v.trim() ? v.trim() : "";
    return { title: str(p.title), message: str(p.message) || str(p.text) };
};
/** Есть ли в payload осмысленный текст (title/message/text). */
const hasMeaningfulPayloadText = (payload) => {
    const { title, message } = readPayloadContent(payload);
    return !!(title || message);
};
/**
 * Типы со СВОИМ осмысленным заголовком (см. describeNotification). `system` и
 * неизвестные типы описываются только текстом из payload; без него уведомление
 * скрывается (см. isHiddenNotification), а не показывается как «Системное сообщение».
 */
const SELF_DESCRIBED_TYPES = new Set([
    "song_ready",
    "song_failed",
    "song_instrumental_ready",
    "song_instrumental_failed",
    "song_wav_ready",
    "song_wav_failed",
    "song_extend_ready",
    "song_extend_failed",
    "song_replace_section_ready",
    "song_replace_section_failed",
    "song_video_cover_ready",
    "song_video_cover_failed",
    "song_karaoke_ready",
    "song_karaoke_failed",
    "voice_ready",
    "voice_phrase_ready",
    "voice_expired",
    "text_ready",
    "text_failed",
    "generation_ready",
    "generation_failed",
    "release_in_progress",
    "release_published",
    "release_rejected",
    "release_message",
    "payment_success",
    "payment_failed",
    "subscription_renewed",
    "subscription_expired",
    "balance_depleted",
    "referral_gift",
]);
/**
 * Канал доставки «веб» в `payload.platforms` (значения Platform-энума бэка;
 * см. domain/enums/platform.py → WEB = "web"). Бот-таргетед уведомления (пинги
 * re-engagement) шлются с `platforms=[tg|max|vk]` БЕЗ web — они предназначены
 * только ботам, поэтому в веб-центре их не показываем (см. isHiddenNotification).
 */
const WEB_CHANNEL = "web";
/**
 * Уведомление таргетировано на бот-каналы (не на веб): в payload есть непустой
 * `platforms` и в нём НЕТ веб-канала. `platforms` отсутствует/пуст → не таргетед
 * (обычное уведомление, показываем как раньше).
 */
const isBotTargetedForNonWeb = (payload) => {
    const raw = payload?.platforms;
    if (!Array.isArray(raw))
        return false;
    const channels = raw
        .map((v) => (typeof v === "string" ? v.toLowerCase() : ""))
        .filter((s) => s.length > 0);
    if (channels.length === 0)
        return false;
    return !channels.includes(WEB_CHANNEL);
};
/**
 * Скрывать ли уведомление из центра/тостов. Прячем: (1) бот-таргетед уведомления
 * (пинги) с `platforms` без web — они не для веба; (2) «пустышки»: `system` или
 * неизвестный тип БЕЗ осмысленного текста в payload — иначе пользователь видел бы
 * заглушку «Уведомление / Системное сообщение». Типы со своим заголовком не трогаем.
 */
export const isHiddenNotification = (n) => {
    // Пинги/бот-таргетед (payload.platforms без web) скрываем всегда — даже если
    // у типа есть свой заголовок (доставка предназначена ботам, не вебу).
    if (isBotTargetedForNonWeb(n.payload))
        return true;
    if (SELF_DESCRIBED_TYPES.has(n.type))
        return false;
    return !hasMeaningfulPayloadText(n.payload);
};
// ── Оплата: пакет и начисления ────────────────────────────────────────────
/**
 * Единицы балансов (ключи `payload.granted` — значения BalanceType бэка, см.
 * `domain/enums/balance.py`; `releases` добавлен вместе с фичей релизов).
 * ru — тройка форм для pluralRu, en — [единственное, множественное].
 */
const BALANCE_UNITS = {
    songs: { ru: ["песня", "песни", "песен"], en: ["song", "songs"] },
    photos: { ru: ["фото", "фото", "фото"], en: ["photo", "photos"] },
    videos: { ru: ["видео", "видео", "видео"], en: ["video", "videos"] },
    generations: {
        ru: ["генерация", "генерации", "генераций"],
        en: ["generation", "generations"],
    },
    congratulations: {
        ru: ["поздравление", "поздравления", "поздравлений"],
        en: ["greeting", "greetings"],
    },
    voice_intro: {
        ru: ["голосовое интро", "голосовых интро", "голосовых интро"],
        en: ["voice intro", "voice intros"],
    },
    singing_seconds: {
        ru: ["секунда поющего фото", "секунды поющего фото", "секунд поющего фото"],
        en: ["singing-photo second", "singing-photo seconds"],
    },
    releases: { ru: ["релиз", "релиза", "релизов"], en: ["release", "releases"] },
};
/**
 * «3 релиза», «5 песен» по ключу баланса. Подписочные типы (`subscription_*`)
 * называем так же, как постоянные — для пользователя это те же единицы.
 * Неизвестный ключ (бэк завёл новый BalanceType) — `null`: молча пропускаем его
 * в перечислении, текст остаётся осмысленным.
 */
const formatGrant = (key, amount, ru) => {
    const unit = BALANCE_UNITS[key.replace(/^subscription_/, "")];
    if (!unit)
        return null;
    const word = ru
        ? pluralRu(amount, unit.ru[0], unit.ru[1], unit.ru[2])
        : amount === 1
            ? unit.en[0]
            : unit.en[1];
    return `${amount} ${word}`;
};
/**
 * Перечисление начислений из `payload.granted` ({"releases": 3, "songs": 5}).
 * Legacy-фолбэк — `songs_granted` (старые уведомления в БД без `granted`).
 *
 * Фолбэк проверяем ПОСЛЕ форматирования, а не по «`granted` пуст»: если бэк
 * начислил только неизвестные фронту ключи, отформатированных частей ноль —
 * тогда `songs_granted` всё ещё лучше, чем пустая строка.
 */
const formatGrants = (payload, ru) => {
    const raw = payload?.granted;
    const entries = [];
    if (raw && typeof raw === "object" && !Array.isArray(raw)) {
        for (const [key, value] of Object.entries(raw)) {
            const amount = readNumber(value);
            if (amount != null)
                entries.push([key, amount]);
        }
    }
    const parts = entries
        .map(([key, amount]) => formatGrant(key, amount, ru))
        .filter((s) => s != null);
    if (parts.length === 0) {
        const legacy = readNumber(payload?.songs_granted);
        const formatted = legacy != null ? formatGrant("songs", legacy, ru) : null;
        if (formatted)
            parts.push(formatted);
    }
    return parts.join(", ");
};
// ── Релиз: сообщение администратора ───────────────────────────────────────
/**
 * Сколько символов сообщения администратора показываем в строке центра.
 * Строка уведомления ничем не обрезана (ни `line-clamp`, ни `text-overflow` —
 * см. NotificationBell.module.scss), поэтому длинное письмо растянуло бы список
 * и тост на пол-экрана. Полный текст всегда доступен в самой заявке.
 */
const RELEASE_MESSAGE_PREVIEW_MAX = 160;
/**
 * Обратная к `html.escape(..., quote=True)` на бэке. Порядок важен: `&amp;`
 * распаковываем последним, иначе «&amp;lt;» превратилось бы в «<».
 */
const unescapeHtmlEntities = (text) => text
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#x27;/g, "'")
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, "&");
/**
 * Текст, который администратор написал по заявке, — из `payload.text`.
 *
 * В payload лежит ГОТОВОЕ бот-сообщение: строка шаблона («✉️ Сообщение по заявке
 * на релиз «X».»), пустая строка, затем сам текст администратора (см.
 * `build_release_notification_text`). Показывать эту склейку целиком нельзя —
 * шаблон дублирует наш заголовок и тащит в интерфейс эмодзи и HTML-сущности.
 * Поэтому берём ХВОСТ после первого двойного перевода строки: у сообщения
 * администратора внутри могут быть свои пустые строки, и делить надо ровно один
 * раз.
 *
 * `null` — сообщения в payload нет (одна строка шаблона): такое возможно у
 * старых/битых записей, и тогда зовём человека открыть заявку, а не показываем
 * ему сырой шаблон.
 */
export const readReleaseAdminMessage = (payload) => {
    const raw = payload?.text;
    if (typeof raw !== "string")
        return null;
    const separator = raw.indexOf("\n\n");
    if (separator < 0)
        return null;
    // Переводы строк схлопываем в пробелы: строка уведомления однострочная, а
    // многострочный текст в ней всё равно склеится без разделителя.
    const message = unescapeHtmlEntities(raw.slice(separator + 2))
        .replace(/\s+/g, " ")
        .trim();
    if (!message)
        return null;
    return message.length > RELEASE_MESSAGE_PREVIEW_MAX
        ? `${message.slice(0, RELEASE_MESSAGE_PREVIEW_MAX).trimEnd()}…`
        : message;
};
/**
 * Текст уведомления об успешной оплате: что за пакет и сколько начислено.
 * Payload беднее (старые уведомления в БД) — деградируем до прежней формулировки.
 */
const describePaymentSuccess = (payload, ru) => {
    const t = (r, e) => (ru ? r : e);
    const rawTitle = payload?.package_title;
    const title = typeof rawTitle === "string" ? rawTitle.trim() : "";
    const grants = formatGrants(payload, ru);
    const head = title
        ? t(`Пакет «${title}» оплачен`, `Package "${title}" paid`)
        : t("Платёж успешно обработан", "Your payment was processed");
    if (!grants)
        return `${head}.`;
    return t(`${head} — начислено: ${grants}.`, `${head} — added: ${grants}.`);
};
/**
 * Человекочитаемое представление уведомления для центра/тоста.
 * Мэтчим по полному NotificationType (`type`); всё, что *_failed / провал —
 * тон error, остальное — success/info.
 */
export const describeNotification = (n, language) => {
    const ru = language === "ru";
    const t = (r, e) => (ru ? r : e);
    const name = readTitle(n.payload);
    // Название из payload.title в кавычках: ёлочки — русская типографика, в EN
    // обычные двойные (как в остальных EN-строках файла).
    const named = (base, suffix) => {
        if (!name)
            return `${base} ${suffix}`;
        return ru
            ? `${base} «${name}» ${suffix}`
            : `${base} "${name}" ${suffix}`;
    };
    const isFailed = n.type.endsWith("_failed") ||
        n.result_status === "failed" ||
        n.type === "subscription_expired";
    const tone = isFailed ? "error" : "success";
    switch (n.type) {
        case "song_ready":
            // Название трека — в заголовок (payload.title), чтобы в списке было видно,
            // какая именно песня готова. Без него — прежняя формулировка.
            return { tone: "success", title: named(t("Песня", "Song"), t("готова", "ready")), message: t("Ваша песня сгенерирована.", "Your song has been generated.") };
        case "song_failed":
            return { tone: "error", title: t("Песня не создана", "Song failed"), message: t("Не удалось сгенерировать песню.", "Failed to generate the song.") };
        case "song_instrumental_ready":
            return { tone: "success", title: t("Минус готов", "Instrumental ready"), message: t("Минусовка доступна в песне.", "The instrumental is available in the song.") };
        case "song_instrumental_failed":
            return { tone: "error", title: t("Минус не создан", "Instrumental failed"), message: t("Не удалось сделать минус.", "Failed to make the instrumental.") };
        case "song_wav_ready":
            return { tone: "success", title: named(t("WAV", "WAV"), t("готов", "ready")), message: t("WAV-версия доступна для скачивания.", "The WAV version is ready to download.") };
        case "song_wav_failed":
            return { tone: "error", title: t("WAV не создан", "WAV failed"), message: t("Не удалось сконвертировать в WAV.", "Failed to convert to WAV.") };
        case "song_extend_ready":
            return { tone: "success", title: named(t("Продление", "Extension"), t("готово", "ready")), message: t("Новая версия появилась в списке.", "The new version has appeared in your list.") };
        case "song_extend_failed":
            return { tone: "error", title: t("Продление не удалось", "Extension failed"), message: t("Баланс возвращён, попробуйте ещё раз.", "Your balance was refunded, please try again.") };
        case "song_replace_section_ready":
            return { tone: "success", title: named(t("Замена секции", "Section replacement"), t("готова", "ready")), message: t("Новая версия появилась в списке.", "The new version has appeared in your list.") };
        case "song_replace_section_failed": {
            // Причину кладёт воркер в payload.reason (too_long / out_of_range /
            // insufficient_balance / код Suno) — известную объясняем человеку.
            const why = replaceSectionFailureText(replaceSectionReason(n.payload?.reason), ru);
            const refund = t("Баланс возвращён, попробуйте ещё раз.", "Your balance was refunded, please try again.");
            // «Недоступно для этого трека» отсекается до списания — без слов о возврате.
            const unsupported = trackActionReasonMessage(n.payload?.reason, ru);
            return { tone: "error", title: t("Замена секции не удалась", "Section replacement failed"), message: unsupported ?? (why ? `${why} ${refund}` : refund) };
        }
        case "song_video_cover_ready":
            return { tone: "success", title: t("Видеообложка готова", "Video cover ready"), message: t("Видео доступно в песне.", "The video is available in the song.") };
        case "song_video_cover_failed":
            return { tone: "error", title: t("Видеообложка не создана", "Video cover failed"), message: t("Баланс возвращён, попробуйте ещё раз.", "Your balance was refunded, please try again.") };
        case "song_karaoke_ready":
            return { tone: "success", title: t("Караоке готово", "Karaoke ready"), message: t("Видео доступно в песне.", "The video is available in the song.") };
        case "song_karaoke_failed":
            // «Недоступно для этого трека» воркер отсекает до списания — без слов о возврате.
            return { tone: "error", title: t("Караоке не создано", "Karaoke failed"), message: trackActionReasonMessage(n.payload?.reason, ru) ?? t("Баланс возвращён, попробуйте ещё раз.", "Your balance was refunded, please try again.") };
        case "voice_ready":
            return { tone: "success", title: t("Голос готов", "Voice ready"), message: name ? t(`Ваш голос «${name}» готов.`, `Your voice "${name}" is ready.`) : t("Ваш голос готов.", "Your voice is ready.") };
        case "voice_phrase_ready":
            return { tone: "info", title: t("Прочитайте контрольную фразу", "Read the phrase"), message: name ? t(`Голос «${name}» ждёт — прочитайте фразу, чтобы продолжить.`, `Voice "${name}" is waiting — read the phrase to continue.`) : t("Голос ждёт — прочитайте контрольную фразу, чтобы продолжить.", "Your voice is waiting — read the phrase to continue.") };
        case "voice_expired":
            return { tone: "warning", title: t("Голос устарел", "Voice expired"), message: name ? t(`Голос «${name}» устарел — пересоздайте его.`, `Voice "${name}" has expired — please recreate it.`) : t("Ваш голос устарел — пересоздайте его.", "Your voice has expired — please recreate it.") };
        case "text_ready":
            return { tone: "success", title: t("Текст готов", "Lyrics ready"), message: t("Текст песни сгенерирован.", "The lyrics have been generated.") };
        case "text_failed":
            return { tone: "error", title: t("Текст не создан", "Lyrics failed"), message: t("Не удалось сгенерировать текст.", "Failed to generate the lyrics.") };
        case "generation_ready":
            return { tone: "success", title: t("Готово", "Ready"), message: t("Генерация завершена.", "The generation is complete.") };
        case "generation_failed":
            return { tone: "error", title: t("Ошибка генерации", "Generation failed"), message: t("Не удалось завершить генерацию.", "Failed to complete the generation.") };
        case "payment_success":
            return { tone: "success", title: t("Оплата прошла", "Payment successful"), message: describePaymentSuccess(n.payload, ru) };
        case "payment_failed":
            return { tone: "error", title: t("Оплата не прошла", "Payment failed"), message: t("Не удалось обработать платёж.", "The payment could not be processed.") };
        case "subscription_renewed":
            return { tone: "success", title: t("Подписка продлена", "Subscription renewed"), message: t("Подписка успешно продлена.", "Your subscription was renewed.") };
        case "subscription_expired":
            return { tone: "warning", title: t("Подписка истекла", "Subscription expired"), message: t("Срок действия подписки закончился.", "Your subscription has expired.") };
        case "balance_depleted":
            return { tone: "warning", title: t("Баланс исчерпан", "Balance depleted"), message: t("Пополните баланс, чтобы продолжить.", "Top up your balance to continue.") };
        case "referral_gift":
            return { tone: "success", title: t("Подарок за друга", "Referral gift"), message: t("Вам начислен бонус за приглашение.", "You received a referral bonus.") };
        // Релиз трека на площадках. Свои тексты обязательны: СЫРОЙ шаблон
        // бот-сообщения (HTML-сущности + эмодзи) лежит в `payload.text`, и без case
        // он протекал в UI через generic-ветку readPayloadContent. А `payload.title`
        // — это название релиза, которое ввёл пользователь (release/endpoints.py:
        // `"title": order.title`), поэтому его подставляем в заголовок, как у
        // остальных типов.
        case "release_in_progress":
            return { tone: "info", title: named(t("Релиз", "Release"), t("в работе", "in progress")), message: t("Заявка принята — трек готовят к публикации.", "Your request was accepted — the track is being prepared for release.") };
        case "release_published":
            return { tone: "success", title: named(t("Релиз", "Release"), t("опубликован", "published")), message: t("Трек вышел на площадках.", "The track is live on streaming services.") };
        case "release_rejected":
            return { tone: "error", title: named(t("Релиз", "Release"), t("отклонён", "rejected")), message: t("Заявку отклонили — откройте её, чтобы увидеть причину.", "The request was rejected — open it to see the reason.") };
        // Сообщение администратора по заявке — единственный тип, где текст ВАЖНЕЕ
        // факта события: человек хочет прочитать, что ему написали, а не «вам
        // ответили». Поэтому в строку идёт сам текст (из `payload.text`, очищенный
        // от шаблонной обёртки), а заголовок называет заявку. Тон нейтральный:
        // сообщение не про успех и не про ошибку.
        case "release_message": {
            const adminMessage = readReleaseAdminMessage(n.payload);
            return {
                tone: "info",
                title: name
                    ? t(`Сообщение по релизу «${name}»`, `Message about release "${name}"`)
                    : t("Сообщение по релизу", "Message about your release"),
                message: adminMessage ??
                    t("Вам написали по заявке — откройте её, чтобы прочитать.", "You have a message about your request — open it to read."),
            };
        }
        case "system":
        default: {
            // `system` / неизвестный тип: показываем текст из payload, если он есть.
            // Без осмысленного текста это generic-заглушка — она НЕ доходит до
            // пользователя (isHiddenNotification фильтрует её из центра и тостов).
            const { title: pTitle, message: pMessage } = readPayloadContent(n.payload);
            const genericTone = n.type === "system" ? "info" : tone;
            if (pTitle || pMessage) {
                return {
                    tone: genericTone,
                    title: pTitle || t("Уведомление", "Notification"),
                    message: pMessage || pTitle,
                };
            }
            return {
                tone: genericTone,
                title: t("Уведомление", "Notification"),
                message: isFailed
                    ? t("Операция не удалась.", "The operation failed.")
                    : t("Обновление по вашему заказу.", "An update on your order."),
            };
        }
    }
};
/** Относительное «сколько назад» для строки уведомления. */
export const formatRelativeTime = (iso, language) => {
    const ru = language === "ru";
    const date = parseServerDate(iso);
    const then = date.getTime();
    if (Number.isNaN(then))
        return "";
    const diffSec = Math.max(0, Math.floor((Date.now() - then) / 1000));
    if (diffSec < 60)
        return ru ? "только что" : "just now";
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60)
        return ru ? `${diffMin} мин назад` : `${diffMin} min ago`;
    const diffHour = Math.floor(diffMin / 60);
    if (diffHour < 24)
        return ru ? `${diffHour} ч назад` : `${diffHour} h ago`;
    const diffDay = Math.floor(diffHour / 24);
    if (diffDay < 7)
        return ru ? `${diffDay} дн назад` : `${diffDay} d ago`;
    return date.toLocaleDateString(ru ? "ru-RU" : "en-US", {
        day: "numeric",
        month: "short",
    });
};
